import request from "../../utils/request";
import { Result } from "../../models/general";

// CO-814 — approval routing for one employee. Mirrors Mowazaf.Server/Data/General/ViewModels/ApprovalRoutingView.cs
// and Controllers/General/ApprovalRoutingController.cs. Both endpoints are Policy="HrOnly" (Admin and Operator
// get 403); another company's user is a 404; HR cannot set bypass on themselves (403).

/**
 * Manager    = the employee's able managers decide.
 * HrFallback = no manager: every HR user of the company (never the requester) decides.
 * ChiefFallback = no manager and no other HR user: the company's Chief users decide (approverNames = Chiefs).
 * Unroutable = no manager, no other HR and no Chief; requests stay pending.
 */
export type ApproverRoute = "Manager" | "HrFallback" | "ChiefFallback" | "Unroutable";

export interface ApprovalRouting {
  userId: string;
  route: ApproverRoute;
  hasManager: boolean;
  /** Up to 10 names; `approverCount` is the real total. */
  approverNames: string[];
  approverCount: number;
  approvalBypass: boolean;
  approvalBypassReason?: string | null;
  /** UTC ISO timestamp — parse with parseApiUtc. */
  approvalBypassSetOnUtc?: string | null;
  approvalBypassSetByName?: string | null;
  /** False when HR is looking at their own profile: nobody may bypass themselves. */
  canEditBypass: boolean;
}

export interface ApprovalBypassInput {
  enabled: boolean;
  /** Required when enabling; max 500 characters. */
  reason?: string;
}

/** Why a request was approved without a human decision (leave / short leave / shift swap views). */
export type AutoApprovalReason = "ApprovalBypass" | "WorkflowAutoApproval" | "PolicyNoApprovalRequired";

/** Max length the server accepts for the bypass reason. */
export const APPROVAL_BYPASS_REASON_MAX = 500;

/** HR only. */
export async function getApprovalRouting(userId: string) {
  const response = await request.get<Result<ApprovalRouting>>(
    `ApprovalRouting/users/${encodeURIComponent(userId)}`,
  );
  return response.data;
}

/** HR only. Every change is written to the security audit log (ApprovalBypassChanged). */
export async function setApprovalBypass(userId: string, input: ApprovalBypassInput) {
  const response = await request.put<Result<ApprovalRouting>>(
    `ApprovalRouting/users/${encodeURIComponent(userId)}/bypass`,
    input,
  );
  return response.data;
}
